import {
  Component,
  OnInit,
  Input,
  ViewEncapsulation,
  Output,
  ViewChild,
  OnDestroy,
} from '@angular/core';
import { EventEmitter } from '@angular/core';
import {
  DateRange,
  MatDatepicker,
  MatDateRangePicker,
} from '@angular/material/datepicker';
import { MatMenuTrigger as MatMenuTrigger } from '@angular/material/menu';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { AuthService } from '../../../core/services/auth.service';
import { GlobalResponsivenessService } from '../../../core/services/global-responsiveness.service';
import { CommonUtils } from '../../utils/common.utils';
import {
  FilterDefaultValues,
  FilterField,
  FilterTypes,
} from './models/filterTypes.model';
import {
  EQUALS,
  NOT_EQUALS,
  GREATER_THAN,
  LESS_THAN,
  GREATER_THAN_EQUAL_TO,
  LESS_THAN_EQUAL_TO,
  CONTAINS,
  START_WITH,
  DOES_NOT_START_WITH,
  CASE_INSENSITIVE_STRING_CONTAINS,
  CASE_INSENSITIVE_STRING_START_WITH,
  CASE_INSENSITIVE_STRING_EQUALS,
  CASE_INSENSITIVE_STRING_NOT_EQUALS,
  CASE_INSENSITIVE_STRING_DOES_NOT_CONTAINS,
  CASE_INSENSITIVE_STRING_DOES_NOT_START_WITH,
} from './models/operators';

@Component({
  selector: 'inforit-filter-menu',
  templateUrl: './inforit-filter-menu.component.html',
  styleUrls: ['./inforit-filter-menu.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class InforitFilterMenuComponent implements OnInit, OnDestroy {
  private static readonly KEY_RANGE_DATE_PICKER = 'rangedatepicker';
  private static readonly KEY_FORM_DATE_RANGE_START = 'start';
  private static readonly KEY_FORM_DATE_RANGE_END = 'end';

  @Input()
  public displayTitle: string;

  @Input()
  public buttonColor: string;

  @Input()
  public backgroundColor: string;

  @Input()
  public fieldDefinitions: FilterTypes[];
  @Input()
  public defaultFilterTypes: FilterTypes[];

  @ViewChild('filterform') public filterForm;
  @ViewChild('validation') public validation;
  @ViewChild(MatMenuTrigger) filterMenuTrigger: MatMenuTrigger;

  @ViewChild('picker') pickerToggle: MatDateRangePicker<null>;
  @ViewChild('datepicker') datePicker: MatDatepicker<null>;

  @Output()
  public onSubmit = new EventEmitter();

  public range = new DateRange<Date>(null, null);

  public color = 'accent';
  public summary = null;
  private erased = false;

  public defaultFieldDefinitions: FilterField[] = [];
  commonUtils = CommonUtils;

  public operators = [
    EQUALS,
    NOT_EQUALS,
    GREATER_THAN,
    LESS_THAN,
    GREATER_THAN_EQUAL_TO,
    LESS_THAN_EQUAL_TO,
    CONTAINS,
    START_WITH,
    DOES_NOT_START_WITH,
    CASE_INSENSITIVE_STRING_CONTAINS,
    CASE_INSENSITIVE_STRING_START_WITH,
    CASE_INSENSITIVE_STRING_EQUALS,
    CASE_INSENSITIVE_STRING_NOT_EQUALS,
    CASE_INSENSITIVE_STRING_DOES_NOT_CONTAINS,
    CASE_INSENSITIVE_STRING_DOES_NOT_START_WITH,
  ];
  subs: Subscription[] = [];
  constructor(
    private globalResponisve: GlobalResponsivenessService,
    private translateService: TranslateService,
    private authService: AuthService,
  ) {}

  ngOnInit() {
    this.subs.push(
      this.authService.timeoutStatus.subscribe(isTimeout => {
        if (isTimeout) {
          if (this.pickerToggle) {
            this.pickerToggle.close();
          }
          if (this.datePicker) {
            this.datePicker.close();
          }
        }
      }),
    );
    this.fieldDefinitions.forEach(element => {
      if (element.value != null && typeof element.value !== 'object') {
        this.addValueToSummary(element.value);
      } else if (
        typeof element.value === 'object' &&
        !!element.value?.begin &&
        !!element.value?.end
      ) {
        this.addValueToSummary(
          this.commonUtils.formatDate(element.value.begin) +
            ' - ' +
            this.commonUtils.formatDate(element.value.end),
        );
      }
    });
    const rangeValue = this.getRangeDatePickerFieldDefinition()?.value;
    if (rangeValue?.begin && rangeValue?.end) {
      this.range = new DateRange<Date>(rangeValue.begin, rangeValue.end);
    }
    this.subs.push(
      this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
        this.showSummary(this.filterForm.value);
      }),
    );
  }
  ngOnDestroy(): void {
    this.subs.forEach(sub => sub.unsubscribe());
  }

  public checkOverride(field: FilterTypes): void {
    if (field.overrideFieldName) {
      const fieldToMakeDefault = this.fieldDefinitions.find(
        definition => definition.name === field.overrideFieldName,
      );
      if (
        fieldToMakeDefault.type ===
        InforitFilterMenuComponent.KEY_RANGE_DATE_PICKER
      ) {
        fieldToMakeDefault.value = null;
        this.range = new DateRange<Date>(null, null);
      } else {
        fieldToMakeDefault.value = FilterDefaultValues.TextDefault;
      }
    }
  }

  public openPicker(): void {
    if (!this.erased) {
      this.pickerToggle.open();
    } else {
      this.erased = false;
    }
  }

  getRangeDatePickerFieldDefinition(): FilterTypes {
    return this.fieldDefinitions.find(
      field => field.type === InforitFilterMenuComponent.KEY_RANGE_DATE_PICKER,
    );
  }

  onModelChangeDataRange() {
    this.getRangeDatePickerFieldDefinition().value = {
      begin: this.range.start,
      end: this.range.end,
    };
  }

  public eraseField(eraseField: FilterTypes) {
    this.erased = true;
    const toEraseField = this.fieldDefinitions.find(
      field => field.name === eraseField.name,
    );
    toEraseField.value = null;

    if (
      toEraseField.type === InforitFilterMenuComponent.KEY_RANGE_DATE_PICKER
    ) {
      this.range = new DateRange<Date>(null, null);
    }
  }
  resetFormFields() {
    this.fieldDefinitions = [
      ...JSON.parse(JSON.stringify(this.defaultFilterTypes)),
    ];

    const dateRange = this.getRangeDatePickerFieldDefinition().value;
    this.range = new DateRange<Date>(dateRange?.begin, dateRange?.end);
  }

  /**
   * This will emit a JSON string to parent component
   * it will also dismiss the filter-menu right after clicking
   * @param formObj the form object
   */
  onFormSubmit(formObj) {
    // reconstruct the dateRange object
    if (formObj.value.start && formObj.value.end) {
      formObj.value.dateRange = {
        begin: formObj.value.start,
        end: formObj.value.end,
      };
      delete formObj.value.start;
      delete formObj.value.end;
    }

    this.ngOnInit();
    this.onSubmit.emit(formObj.value);
    this.showSummary(formObj.value);
    this.cancelAction();
  }

  showSummary(form) {
    this.summary = null;
    if (form == null) return;
    var values = Object.keys(form).map(function (index) {
      let value = form[index];
      return value;
    });
    values.forEach(value => {
      if (value && value !== null) {
        if (typeof value !== 'object') {
          this.translateService
            .get(value)
            .subscribe(result => this.addValueToSummary(result));
        } else {
          this.addValueToSummary(
            this.commonUtils.formatDate(value.begin) +
              ' - ' +
              this.commonUtils.formatDate(value.end),
          );
        }
      }
    });
  }

  addValueToSummary(value) {
    this.translateService.get(value).subscribe(t => {
      if (this.summary == null && t !== '-') {
        this.summary = t;
      } else if (t !== '-') {
        this.summary = this.summary + ', ' + t;
      }
    });
  }

  /**
   * this closes the menu
   */
  cancelAction() {
    this.filterMenuTrigger.closeMenu();
  }

  /**
   * Resets the form to its initial values
   */
  resetAction() {
    this.resetFormFields();
    this.filterForm.resetForm();
  }

  formatSummary() {
    if (this.summary !== null) {
      return this.globalResponisve.formatFilterSummary(this.summary);
    } else return null;
  }
}
